import React, { useState, useEffect } from 'react';

// Import images from the src/assets/ folder
import bell from '../assets/bell.png';
import gh from '../assets/gh.png';
import tx from '../assets/tx.png';
import gfg from '../assets/gfgf.jpg';
import re from '../assets/re.jpg';
import cp from '../assets/cp.png';
import reactLogo from '../assets/react2.webp';
import node from '../assets/node.webp';
import tf from '../assets/tf.png';
import sanity from '../assets/sanity.png';
import mongo from '../assets/OIP.jpg';
import firebase from '../assets/f.webp';
import kubernetes from '../assets/k.png';
import html from '../assets/R.jpg';
import tailwind from '../assets/t.png';
import php from '../assets/php.svg.png';
import docker from '../assets/docker.png';
import git from '../assets/git_github.png';
import rest from '../assets/rest.jpg';
import azure from '../assets/az.webp';
import express from '../assets/express.png';
import postman from '../assets/postman.png';
import ml from '../assets/machine-learning-thumbnail.jpg';
import redux from '../assets/redux.png';

const Card = ({ image, title, description, isBlurred, isPaper, paperLink, publishStatus, url = "#", isPublishedPaper = false }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  const isUnpublished = publishStatus === 'unpublished';

  return (
    <div 
      className={`bg-white border-2 border-black overflow-hidden transition-transform hover:scale-105 rounded-lg relative`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="p-4 sm:p-6">
        <div className={`aspect-square relative mb-3 ${(isBlurred || (isPaper && isHovered && !isPublishedPaper) || isUnpublished || (isHovered && !isPaper)) ? 'blur-sm' : ''} transition-all duration-300`}>
          <img src={image} alt={title} className="w-full h-full object-cover rounded-lg border-2 border-black" />
        </div>
        <div className={`text-center ${isBlurred ? 'blur-sm' : ''}`}>
          <h3 className="font-medium text-base sm:text-lg mb-2">{title}</h3>
          <p className="text-gray-600 text-xs sm:text-sm relative">
            {description}
            {isPublishedPaper && (
              <a 
                href={url} 
                className="absolute right-0 -top-1 ml-2 transform transition-transform hover:translate-y-[-3px]"
                title="View Paper"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  className="text-black"
                >
                  <line x1="7" y1="17" x2="17" y2="7"></line>
                  <polyline points="7 7 17 7 17 17"></polyline>
                </svg>
              </a>
            )}
          </p>
        </div>
      </div>
      
      {isHovered && !isPaper && (
         <div className="absolute inset-0 flex items-center justify-center">
         <a 
           href={url} 
           className="flex items-center justify-center px-4 sm:px-8 py-2 text-2xl sm:text-4xl bg-black no-underline h-[60px] w-[160px] sm:h-[80px] sm:w-[200px] text-white font-bold hover:bg-gray-800 transition-colors duration-300"
         >
           LIVE
         </a>
       </div>
      )}
      
      {isUnpublished && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="px-4 py-2 bg-black bg-opacity-70 text-white font-bold text-sm sm:text-base rounded-lg">
            Yet to be published
          </div>
        </div>
      )}
    </div>
  );
};

const Projects = () => {
  const [activeSection, setActiveSection] = useState('PROJECTS');
  const [imagesLoaded, setImagesLoaded] = useState(false);

  const skills = [
    { name: 'Competitive Programming', icon: cp },
    { name: 'React', icon: reactLogo },
    { name: 'Node.js', icon: node },
    { name: 'TensorFlow', icon: tf },
    { name: 'Sanity', icon: sanity },
    { name: 'MongoDB', icon: mongo },
    { name: 'Firebase', icon: firebase },
    { name: 'Kubernetes', icon: kubernetes },
    { name: 'HTML5', icon: html },
    { name: 'TailwindCSS', icon: tailwind },
    { name: 'Git', icon: git },
    { name: 'Rest API', icon: rest },
    { name: 'Azure', icon: azure },
    { name: 'Express.js', icon: express },
    { name: 'Postman', icon: postman },
  ];

  // Preload all skill images
  useEffect(() => {
    const preloadImages = () => {
      const promises = skills.map(skill => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.src = skill.icon;
          img.onload = resolve;
          img.onerror = reject;
        });
      });
      
      Promise.all(promises)
        .then(() => setImagesLoaded(true))
        .catch(error => {
          console.error("Failed to load some images", error);
          setImagesLoaded(true); // Set to true anyway to show content
        });
    };
    
    preloadImages();
  }, []);

  return (
    <div className="min-h-screen bg-[#dde74e] p-4 sm:p-8">
      <div className="max-w-7xl mx-auto">
        
        {/* Navigation Buttons - Responsive Version */}
        <div className="flex flex-col sm:flex-row justify-center sm:space-x-4 md:space-x-8 lg:space-x-16 mb-8 sm:mb-16 space-y-3 sm:space-y-0 overflow-hidden">
          {['PROJECTS', 'SKILLS', 'RESEARCH PAPER'].map((section) => (
            <button
              key={section}
              onClick={() => setActiveSection(section)}
              className={`px-4 py-2 rounded-lg border-2 border-black font-bold text-sm sm:text-base md:text-lg transition-colors ${
                activeSection === section ? 'bg-black text-white' : 'bg-white text-black'
              }`}
            >
              {section.replace('_', ' ')}
            </button>
          ))}
        </div>

        {/* Content Sections */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          
          {activeSection === 'PROJECTS' && (
            <>
              <Card 
                image={bell} 
                title="Book Club Management System" 
                description="Subscription-Based Reading Hub with booming community."
                url="https://bell-book-candle.vercel.app/" 
              />
              <Card 
                image={gfg} 
                title="Public Club Chronicle Website" 
                description="A distinguished digital gateway showcasing our club's legacy, offerings, and thriving intellectual community."
                url="https://gfgsrmrmp.vercel.app/" 
              />
              <Card 
                image={gh} 
                title="Coding Community Management Platform" 
                description="Platform to empower coders with seamless contest tracking, performance insights, and growth opportunities."
                url="https://example.com/riviera" 
              />
            </>
          )}

          {activeSection === 'SKILLS' && (
            <div className="col-span-full">
              {imagesLoaded ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 md:gap-6">
                  {skills.map((skill, index) => (
                    <div 
                      key={index} 
                      className="bg-white border-2 border-black rounded-lg py-2 px-0 h-24 sm:h-28 md:h-32 flex flex-col items-center justify-center gap-2 hover:scale-105 transition-transform"
                    >
                      <img 
                        src={skill.icon} 
                        alt={skill.name} 
                        className="w-full h-full object-contain p-1"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-t-4 border-b-4 border-black"></div>
                </div>
              )}
            </div>
          )}

          {activeSection === 'RESEARCH PAPER' && (
            <>
              <Card 
                image={tx} 
                title="AI & FinanceTech" 
                description="ARTIFY:AI-Powered Platform for Antique Valuation, Authentication, and Financial Assessment" 
                isPaper={true}
                url="https://github.com/AMISHA2004-devgeek/Artify"
                isPublishedPaper={true}
              />
              <Card 
                image={re} 
                title="Machine Learning" 
                description="Research on deep learning algorithms for deep fake analysis" 
                isPaper={true}
                publishStatus="unpublished"
              />
              <Card 
                image={re} 
                title="IOT" 
                description="Specific sensors and techniques to bring about a boom in MicroIrrigation Culture " 
                isPaper={true}
                publishStatus="unpublished"
              />
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default Projects;